/**
 * @voltxampere/web — `<vxa-statusbar>` (Sprint 11).
 *
 * Bottom bar — version chip + ready signal + symbol count
 * + selection count placeholder + locale chip. Pure read-only
 * for Sprint 11; Sprint 13+ adds engine connection status.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";
import { ALL_SYMBOLS } from "@voltxampere/ui";

import { selectionCount } from "../store/ui.js";
import { currentLocale } from "../store/locale.js";
import { t } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";

const SHELL_VERSION = "v0.1.0";

@customElement("vxa-statusbar")
export class VxaStatusbar extends LitElement {
  // Subscribe to both signals so the bar re-renders when locale
  // toggles (string lookup) or selection count changes.
  private readonly _selection = new SignalController(this, selectionCount);
  private readonly _locale = new SignalController(this, currentLocale);

  static override styles = css`
    :host {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      gap: var(--s-5);
      height: var(--statusbar-height);
      padding: 0 var(--s-5);
      border-top: 1px solid var(--ivory-wisp);
      background: var(--ink);
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      color: var(--ivory-dim);
      text-transform: uppercase;
      user-select: none;
      flex-shrink: 0;
    }

    .left,
    .right {
      display: flex;
      align-items: center;
      gap: var(--s-4);
    }

    .right {
      justify-content: flex-end;
    }

    .ready::before {
      content: "";
      display: inline-block;
      width: 6px;
      height: 6px;
      background: var(--success);
      margin-right: var(--s-2);
      vertical-align: middle;
    }

    .version {
      color: var(--brass);
    }

    .selection {
      color: var(--ivory-faint);
    }
  `;

  override render() {
    const symbolsLabel = t("statusbar.symbols", { count: String(ALL_SYMBOLS.length) });
    const selectionLabel = t("statusbar.selection", {
      count: String(this._selection.value),
    });

    return html`
      <div class="left">
        <span class="ready">${t("statusbar.ready")}</span>
        <span class="version">${SHELL_VERSION}</span>
      </div>
      <div class="center"></div>
      <div class="right">
        <span class="symbols">${symbolsLabel}</span>
        <span class="selection">${selectionLabel}</span>
        <span class="locale">${t("statusbar.locale")}</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-statusbar": VxaStatusbar;
  }
}
